import { useFormikContext } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDistricts } from "../../redux/slices/CommonSlice";

import uploadImage from "../../assets/images/upload.svg";
import { useTranslation } from "react-i18next";
import ImageFilePreview from "../../components/ImageFilePreview";
import { toast } from "react-toastify";
import Select from "react-select";
import document_img from "../../assets/images/document-img.jpg";
import PreviewModal from "../../components/PreviewModal";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { useLoadScript } from "@react-google-maps/api";

const Step2 = () => {
  const { t } = useTranslation();
  const { handleChange, handleBlur, values, touched, errors, setFieldValue } =
    useFormikContext();

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API,
    libraries: ["places"],
  });

  const { districts } = useSelector((state) => state.commonStore);

  const dispatch = useDispatch();
  const [nationalAdrressAttachmentType, setNationalAdrressAttachmentType] = useState(null);
  const [nationalAdrressAttachments, setNationalAdrressAttachments] = useState(null);
  const [previewModal, setPreviewModal] = useState({
    show: false,
    title: "",
    fileUrl: "",
    fileType: "",
  });
  const [coordinates, setCoordinates] = useState({ lat: "", lng: "" });
  const latestSelectedPlaceIdRef = useRef(null);

  const getFileTypeFromUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    const ext = url.split('.').pop().toLowerCase();
    if (["png", "jpg", "jpeg"].includes(ext)) return "image";
    if (ext === "pdf") return "pdf";
    if (ext === "doc" || ext === "docx") return "doc";
    return "";
  };

  const handleViewDocument = (title, attachment, attachmentType, formikValue) => {
    let fileUrl = "";
    let fileType = "";
    if (attachment && typeof attachment === "string") {
      fileUrl = attachment;
      fileType = attachmentType?.startsWith("image/")
        ? "image"
        : attachmentType?.includes("pdf")
          ? "pdf"
          : attachmentType?.includes("word") || attachmentType?.includes("doc")
            ? "doc"
            : "";
    } else if (formikValue) {
      // If formikValue is a File object, create an object URL
      if (formikValue instanceof File) {
        fileUrl = URL.createObjectURL(formikValue);
        fileType = formikValue.type?.startsWith("image/")
          ? "image"
          : formikValue.type?.includes("pdf")
            ? "pdf"
            : formikValue.name?.toLowerCase().endsWith(".doc") || formikValue.name?.toLowerCase().endsWith(".docx")
              ? "doc"
              : "";
      } else if (typeof formikValue === "string") {
        fileUrl = formikValue;
        fileType = getFileTypeFromUrl(fileUrl);
      }
    }

    // DOC/DOCX open in new tab
    if (fileType === "doc") {
      const officeUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`;
      window.open(officeUrl, "_blank", "noopener,noreferrer");
    } else {
      setPreviewModal({
        show: true,
        title,
        fileUrl,
        fileType,
      });
    }
  };


  // Re-initialize preview state from Formik File object when component mounts
  useEffect(() => {
    const file = values?.nationalAdrressAttachment;
    if (file instanceof File && !nationalAdrressAttachments) {
      const fileType = file.type;
      setNationalAdrressAttachmentType(fileType);
      if (fileType?.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setNationalAdrressAttachments(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        const fileURL = URL.createObjectURL(file);
        setNationalAdrressAttachments(fileURL);
      }
    }
  }, [values?.nationalAdrressAttachment]);

  useEffect(() => {
    dispatch(fetchDistricts());
  }, [dispatch]);

  const handleRemoveNationalDocument = () => {
    setNationalAdrressAttachments(null);
    setNationalAdrressAttachmentType(null);
    setFieldValue("nationalAdrressAttachment", null);
    // Clear the file input
    const fileInput = document.getElementById("fileInput1");
    if (fileInput) fileInput.value = "";
  };

  const handleNationalDocumentChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const allowedExtensions = [".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx"];

    const fileTypeValid = allowedTypes.includes(file.type);
    const fileExtensionValid = allowedExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );

    if (!fileTypeValid || !fileExtensionValid) {
      toast.error(
        t("Invalid file type. Supported formats: .png, .jpg, .pdf, .doc, .docx.")
      );
      e.target.value = "";
      return;
    }

    if (file) {
      const fileType = file.type;
      setNationalAdrressAttachmentType(fileType);

      // Store object URL for preview
      if (fileType.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setNationalAdrressAttachments(reader.result); // Base64 image
        };
        reader.readAsDataURL(file);
      } else {
        const fileURL = URL.createObjectURL(file);
        setNationalAdrressAttachments(fileURL); // Object URL for PDF/DOC
      }

      setFieldValue("nationalAdrressAttachment", file);
    }
  };

  const districtOptions = districts.map(district => ({
    value: district.id,
    label: district.name
  }));

  const handleSelect = (place) => {
    if (!place?.value?.place_id) {
      console.error("No place_id in selected place:", place);
      return;
    }
    latestSelectedPlaceIdRef.current = place.value.place_id;

    if (!isLoaded || !window.google?.maps?.places?.PlacesService) {
      console.error("Google Maps JS API not loaded.");
      return;
    }

    const service = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );

    const request = {
      placeId: place.value.place_id,
      fields: ["formatted_address", "geometry"],
    };

    service.getDetails(request, (result, status) => {
      if (latestSelectedPlaceIdRef.current !== place.value.place_id) {
        return;
      }
      if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
        console.error("Error fetching place details:", status, result);
        return;
      }
      const { formatted_address, geometry } = result;
      const lat = geometry.location.lat();
      const lng = geometry.location.lng();
      setCoordinates({ lat, lng });
      setFieldValue("street", formatted_address);
      setFieldValue("latitude", String(lat));
      setFieldValue("longitude", String(lng));
    });
  };

  return (
    <>
      <div className="mb-3">
        {/* <label for="companyAddress" className="form-label visually-hidden">
          Building No.
        </label> */}
        {/* <input
          type="text"
          className="form-control"
          id="buildingNo"
          name="buildingNo"
          placeholder={`${t("Building No.")} *`}
          required
          onChange={handleChange}
          value={values.buildingNo}
          onBlur={handleBlur}
        />
        {touched.buildingNo && errors.buildingNo && (
          <div className="text-danger">{errors.buildingNo}</div>
        )} */}
      </div>
      <div className="mb-3">
        {/* <label for="companyAddress" className="form-label visually-hidden">
          Street
        </label> */}
        {/* <input
          type="text"
          className="form-control"
          id="street"
          name="street"
          placeholder={`${t("Street")} *`}
          required
          onChange={handleChange}
          value={values.street}
          onBlur={handleBlur}
        /> */}

        {/*  working below but currenlty commented */}
        {isLoaded ? (
          <GooglePlacesAutocomplete
            apiKey={process.env.REACT_APP_GOOGLE_MAP_API}
            selectProps={{
              onChange: handleSelect,
              // placeholder: t("Search for Street..."),
              placeholder: t("Short Address"),
              isClearable: true,
              noOptionsMessage: () => t("No Options"),
              loadingMessage: () => t("Loading..."),
              value: values.street
                ? { label: values.street, value: values.street }
                : null,
            }}
            autocompletionRequest={{
              componentRestrictions: {
                country: ["sa"],
              },
            }}
          />
        ) : (
          <div className="form-control" style={{ height: "38px" }}></div>
        )}
        {touched.street && errors.street && (
          <div className="text-danger">{errors.street}</div>
        )}
      </div>

      <div className="mb-3">
        {/* <label for="companyAddress" className="form-label visually-hidden">
          Secondary No.
        </label> */}
        {/* <input
          type="text"
          className="form-control"
          id="secondaryNo"
          name="secondaryNo"
          placeholder={`${t("Secondary No.")} *`}
          required
          onChange={handleChange}
          value={values.secondaryNo}
          onBlur={handleBlur}
        />
        {touched.secondaryNo && errors.secondaryNo && (
          <div className="text-danger">{errors.secondaryNo}</div>
        )} */}
      </div>

      <div className="row mb-3">
        {/* <div className="col-6">
          <Select
            name="district"
            options={districtOptions}
            onChange={(selectedOption) => {
              setFieldValue("district", selectedOption ? selectedOption.value : "");
            }}
            value={districtOptions.find(option => option.value === values.district) || null}
            className="basic-single"
            classNamePrefix="select"
            isClearable
            isSearchable
            placeholder={t("Select District")}
            noOptionsMessage={() => t("No Options")}
          />
          {touched.district && errors.district && (
            <div className="text-danger">{errors.district}</div>
          )}
        </div> */}
        {/* <div className="col-6">
          <input
            type="text"
            className="form-control"
            id="postalCode"
            name="postalCode"
            placeholder={`${t("Postal code")} *`}
            onChange={handleChange}
            value={values.postalCode}
            onBlur={handleBlur}
          />
          {touched.postalCode && errors.postalCode && (
            <div className="text-danger">{errors.postalCode}</div>
          )}
        </div> */}
      </div>
      {/* <div className="row mb-3">
        <div className="col-12">
          <input
            type="text"
            className="form-control"
            id="short_address"
            name="short_address"
            placeholder={`${t("Short Address")} *`}
            onChange={handleChange}
            value={values.short_address}
            onBlur={handleBlur}
          />
          {touched.short_address && errors.short_address && (
            <div className="text-danger">{errors.short_address}</div>
          )}
        </div>
      </div> */}

      <div className="mb-3">
        {/* <label for="nationalAddress" className="form-label visually-hidden">
          National Address
        </label> */}
        {/* <input
          type="text"
          className="form-control"
          id="nationalAddress"
          name="nationalAddress"
          placeholder={`${t("National Address")} *`}
          required
          onChange={handleChange}
          value={values.nationalAddress}
          onBlur={handleBlur}
        />
        {touched.nationalAddress && errors.nationalAddress && (
          <div className="text-danger">{errors.nationalAddress}</div>
        )} */}
      </div>

      <div className="upload-container d-flex align-items-center mb-3">
        <div
          className="upload-box"
          onClick={() => document.getElementById("fileInput1").click()}
        >
          {/* {values?.nationalAdrressAttachment ? (
            <ImageFilePreview
              file={values.nationalAdrressAttachment}
              width="40"
              height="40"
            />
          ) : (
            <img src={uploadImage} alt={t("Upload Icon")} className="upload-icon" />
          )} */}

          {values?.nationalAdrressAttachment && !nationalAdrressAttachments ? (
            <img
              src={values?.nationalAdrressAttachment}
              alt={t("Upload Icon")}
              className="upload-icon"
            />
          ) : nationalAdrressAttachmentType && !nationalAdrressAttachmentType.startsWith("image/") ? (
            <a href={nationalAdrressAttachments} target="_blank" rel="noopener noreferrer">
              <img
                src={document_img}
                alt="Document Icon"
                className="upload-icon"
                style={{ cursor: "pointer" }}
              />
            </a>
          ) : (
            nationalAdrressAttachments ?
              <img
                src={nationalAdrressAttachments}
                alt="national address document uploaded successfully"
              />
              :
              <img
                src={uploadImage}
                alt={t("Upload Icon")}
                className="upload-icon"
              />
          )}

          <input
            type="file"
            id="fileInput1"
            style={{ display: "none" }}
            onChange={(e) => handleNationalDocumentChange(e)}
            accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
          />
        </div>
        <div className="upload-description ms-4">
          <p>{`${t("National address document")}`}</p>


          {values?.nationalAdrressAttachment ? (
            <>
              <p className="mb-0">{t("File Uploaded Successfully")}</p>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-link text-decoration-none"
                  style={{ padding: 0 }}
                  onClick={() =>
                    handleViewDocument(
                      t("National address document"),
                      nationalAdrressAttachments,
                      nationalAdrressAttachmentType,
                      values?.nationalAdrressAttachment
                    )
                  }
                >
                  {t("View Document")}
                </button>
                <span className="text-muted">|</span>
                <button
                  type="button"
                  className="btn text-danger text-underline-none"
                  style={{ padding: 0 }}
                  onClick={handleRemoveNationalDocument}
                >
                  {t("Remove Document")}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="mb-0">{t("Maximum file size: Up to 2 MB")}</p>
              <p className="mb-0">
                {t("Supported format: .png, .jpg, .pdf .doc")}
              </p>
            </>
          )}
        </div>
      </div>
      {touched.nationalAdrressAttachment &&
        errors.nationalAdrressAttachment && (
          <div className="text-danger">{errors.nationalAdrressAttachment}</div>
        )}
      <PreviewModal
        show={previewModal.show}
        onHide={() => setPreviewModal({ ...previewModal, show: false })}
        title={previewModal.title}
        fileUrl={previewModal.fileUrl}
        fileType={previewModal.fileType}
      />
    </>
  );
};

export default Step2;

// <div className="row mb-3">
// <div className="col-6">
//   <label for="country" className="form-label visually-hidden">
//     Country
//   </label>
//   <select
//     className="form-control"
//     id="country"
//     name="country"
//     required
//     onChange={handleChange}
//     value={values.country}
//     onBlur={handleBlur}
//   >
//     <option value="" selected>
//       Select Country
//     </option>
//     {countries?.map((country, i) => {
//       return (
//         <option key={i} value={country.id}>
//           {country.name}
//         </option>
//       );
//     })}
//   </select>
//   {touched.country && errors.country && (
//     <div className="text-danger">{errors.country}</div>
//   )}
// </div>
// <div className="col-6">
//   <label for="city" className="form-label visually-hidden">
//     City
//   </label>
//   <select
//     className="form-control"
//     id="city"
//     name="city"
//     required
//     onChange={handleChange}
//     value={values.city}
//     onBlur={handleBlur}
//   >
//     <option disabled selected>
//       Select City
//     </option>
//     {cities?.map((city, i) => {
//       return (
//         <option key={i} value={city.id}>
//           {city.name}
//         </option>
//       );
//     })}
//   </select>
//   {touched.city && errors.city && (
//     <div className="text-danger">{errors.city}</div>
//   )}
// </div>
// </div>
